/**
 * Live stock, because the page's own answer goes stale.
 *
 * Whether a product is buyable is decided when Shopify renders the page and baked into the HTML.
 * Two things then rot it, and neither is fixable by rendering harder:
 *
 *   1. The browser keeps its own copy of the page. Shopify sends storefront HTML with no
 *      `cache-control` (only `cdn-cache-control`, which is the CDN's, not the browser's), and a
 *      theme cannot set response headers — so a shopper can be served a page built before the
 *      item sold out. This is exactly how a sold-out lamp kept offering "Personalize now".
 *   2. A page that was right when it loaded is wrong ten minutes later, when the last one sells.
 *
 * So the render is the fast first paint and this is the truth. `/products/<handle>.js` is
 * Shopify's own product JSON — small (5–9 KB), always `no-cache, no-store`, no app and no
 * credentials — and it is asked for with `cache: 'no-store'` so nothing between here and Shopify
 * can answer from a shelf.
 *
 * Two passes, priced to what they cover:
 *   - page load: one request, the product this page is about, so the buy button is right;
 *   - builder open: the handles a bundle or add-on could add, which most visitors never reach.
 *
 * Fail open, always. A refused or malformed response changes nothing on the page — the server's
 * answer stands. A network blip must never turn a working store into one that looks sold out.
 */
(function (global) {
  'use strict';

  var PRODUCT_JSON_SUFFIX = '.js';
  var SOLD_OUT_LABEL = 'Sold out';
  /** One-shot per handle: a stale page that is actually back in stock reloads once, never twice. */
  var RELOAD_FLAG_PREFIX = 'pb_live_stock_reloaded::';

  function storefrontRoot(global_) {
    var root = global_ && global_.Shopify && global_.Shopify.routes && global_.Shopify.routes.root;
    if (typeof root !== 'string' || !root) return '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  /** Locale/market aware, because the root carries the market prefix Shopify put the shopper on. */
  function productJsonUrl(handle, root) {
    return String(root || '/') + 'products/' + encodeURIComponent(String(handle)) + PRODUCT_JSON_SUFFIX;
  }

  /**
   * Narrow Shopify's product JSON to the only two things this file cares about. Anything that is
   * not a usable answer returns null, which every caller treats as "leave the page alone".
   */
  function readAvailability(json) {
    if (!json || typeof json !== 'object') return null;
    if (typeof json.available !== 'boolean') return null;
    var variants = {};
    var list = Array.isArray(json.variants) ? json.variants : [];
    for (var i = 0; i < list.length; i++) {
      var v = list[i];
      if (v && v.id != null) variants[String(v.id)] = v.available !== false;
    }
    return { handle: String(json.handle || ''), available: json.available, variants: variants };
  }

  function cleanHandles(handles) {
    var seen = Object.create(null);
    var out = [];
    (handles || []).forEach(function (raw) {
      if (raw == null) return;
      var handle = String(raw).trim();
      if (!handle || seen[handle]) return;
      seen[handle] = true;
      out.push(handle);
    });
    return out;
  }

  /**
   * Every product a bundle or add-on could put in the cart. The main product is deliberately not
   * included: the page-load pass already has it, and asking twice buys nothing.
   */
  function collectBundleHandles(data) {
    var handles = [];
    var bundles = (data && data.bundles) || [];
    for (var i = 0; i < bundles.length; i++) {
      var items = (bundles[i] && bundles[i].items) || [];
      for (var j = 0; j < items.length; j++) {
        if (items[j]) {
          handles.push(items[j].handle);
          var itemAddons = items[j].addons || [];
          for (var k = 0; k < itemAddons.length; k++) {
            if (itemAddons[k]) handles.push(itemAddons[k].handle);
          }
        }
      }
    }
    var mainAddons = (data && data.mainProduct && data.mainProduct.addons) || [];
    for (var m = 0; m < mainAddons.length; m++) {
      if (mainAddons[m]) handles.push(mainAddons[m].handle);
    }
    return cleanHandles(handles);
  }

  /**
   * Fetch each handle in parallel. One failure is one missing answer, never a rejected promise:
   * the map simply has no entry for that handle and the server's flag keeps standing.
   */
  function fetchAvailability(handles, deps) {
    var opts = deps || {};
    var doFetch = opts.fetch || (global.fetch && global.fetch.bind(global));
    var root = opts.root || storefrontRoot(global);
    var list = cleanHandles(handles);
    if (!doFetch || !list.length) return Promise.resolve({});

    return Promise.all(
      list.map(function (handle) {
        return doFetch(productJsonUrl(handle, root), {
          cache: 'no-store',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        })
          .then(function (res) {
            if (!res || !res.ok) return null;
            return res.json();
          })
          .then(function (json) {
            var availability = readAvailability(json);
            return availability ? { handle: handle, availability: availability } : null;
          })
          .catch(function () {
            return null;
          });
      })
    ).then(function (results) {
      var map = {};
      results.forEach(function (entry) {
        if (entry) map[entry.handle] = entry.availability;
      });
      return map;
    });
  }

  /** Write one live answer onto one line item or add-on. Returns true when it actually moved. */
  function applyToLine(line, availabilityByHandle) {
    if (!line || line.handle == null) return false;
    var live = availabilityByHandle[String(line.handle)];
    if (!live) return false;
    if (line.available === live.available) return false;
    line.available = live.available;
    return true;
  }

  /**
   * Patch every bundle line and add-on from the live map, then hand the data back to the filter so
   * `soldOut` is restamped from what is true now rather than what was true at render.
   */
  function applyToBuilderData(data, availabilityByHandle) {
    if (!data || !availabilityByHandle) return false;
    var changed = false;
    var bundles = data.bundles || [];

    for (var i = 0; i < bundles.length; i++) {
      var items = (bundles[i] && bundles[i].items) || [];
      for (var j = 0; j < items.length; j++) {
        if (applyToLine(items[j], availabilityByHandle)) changed = true;
        var itemAddons = (items[j] && items[j].addons) || [];
        for (var k = 0; k < itemAddons.length; k++) {
          if (applyToLine(itemAddons[k], availabilityByHandle)) changed = true;
        }
      }
    }

    var mainAddons = (data.mainProduct && data.mainProduct.addons) || [];
    for (var m = 0; m < mainAddons.length; m++) {
      if (applyToLine(mainAddons[m], availabilityByHandle)) changed = true;
    }
    if (applyToLine(data.mainProduct, availabilityByHandle)) changed = true;

    // The snapshot the filter restores from on every re-apply. Left unpatched, the next apply
    // would quietly undo everything above.
    if (changed && Array.isArray(data._pbBundlesOriginal)) {
      var originals = data._pbBundlesOriginal;
      for (var o = 0; o < originals.length; o++) {
        var origItems = (originals[o] && originals[o].items) || [];
        for (var p = 0; p < origItems.length; p++) {
          applyToLine(origItems[p], availabilityByHandle);
          var origAddons = (origItems[p] && origItems[p].addons) || [];
          for (var q = 0; q < origAddons.length; q++) applyToLine(origAddons[q], availabilityByHandle);
        }
      }
    }
    return changed;
  }

  /**
   * The buy button, in the one direction that can be done safely in place.
   *
   * Sold out now → the button is disabled and relabelled. That is the harmful direction: a shopper
   * personalises everything and is refused at the till.
   *
   * Back in stock on a stale page → the original label came from metaobjects this file never saw,
   * so it cannot be rebuilt here. One reload gets the real page instead, guarded by a per-handle
   * session flag so a page that keeps rendering sold out can never loop.
   */
  function applyToTriggerButton(btn, availability, deps) {
    if (!btn || !availability) return 'unchanged';
    var wasSoldOut = btn.getAttribute('data-pb-sold-out') === '1';

    if (availability.available === false) {
      if (wasSoldOut) return 'unchanged';
      btn.setAttribute('data-pb-sold-out', '1');
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('disabled', 'disabled');
      btn.disabled = true;
      btn.classList.remove('pb-loading');
      btn.classList.add('builder-trigger-btn--sold-out');
      var icon = btn.querySelector('.pb-trigger-icon');
      if (icon && icon.parentNode) icon.parentNode.removeChild(icon);
      var spinner = btn.querySelector('.pb-trigger-spinner');
      if (spinner && spinner.parentNode) spinner.parentNode.removeChild(spinner);
      var label = btn.querySelector('.pb-trigger-text');
      if (label) {
        label.removeAttribute('data-pb-btn-template');
        label.textContent = SOLD_OUT_LABEL;
      } else {
        btn.textContent = SOLD_OUT_LABEL;
      }
      return 'sold-out';
    }

    if (!wasSoldOut) return 'unchanged';
    var opts = deps || {};
    var storage = opts.storage;
    var key = RELOAD_FLAG_PREFIX + String(availability.handle || btn.getAttribute('data-pb-stock-handle') || '');
    try {
      if (storage && storage.getItem(key)) return 'unchanged';
      if (storage) storage.setItem(key, '1');
    } catch (e) {
      /* private mode: without a place to remember the reload, do not risk one */
      return 'unchanged';
    }
    if (typeof opts.reload === 'function') opts.reload();
    return 'reload';
  }

  /* ------------------------------------------------------------------ runtime */

  function readStockHandle(doc) {
    var host = doc.querySelector('[data-pb-stock-handle]');
    return host ? host.getAttribute('data-pb-stock-handle') : '';
  }

  function refreshTriggerButton() {
    var doc = global.document;
    if (!doc) return Promise.resolve('unchanged');
    var handle = readStockHandle(doc);
    var btn = doc.getElementById('openBuilderBtn');
    if (!handle || !btn) return Promise.resolve('unchanged');
    return fetchAvailability([handle]).then(function (map) {
      var live = map[handle];
      if (!live) return 'unchanged';
      var data = global.BUILDER_DATA;
      if (data) applyToBuilderData(data, map);
      return applyToTriggerButton(btn, live, {
        storage: safeSessionStorage(),
        reload: function () {
          global.location.reload();
        }
      });
    });
  }

  function safeSessionStorage() {
    try {
      return global.sessionStorage || null;
    } catch (e) {
      return null;
    }
  }

  var bundlePassDone = false;

  function refreshBundleStock() {
    if (bundlePassDone) return Promise.resolve(false);
    bundlePassDone = true;
    var data = global.BUILDER_DATA;
    if (!data) return Promise.resolve(false);
    var handles = collectBundleHandles(data);
    if (!handles.length) return Promise.resolve(false);
    return fetchAvailability(handles).then(function (map) {
      if (!applyToBuilderData(data, map)) return false;
      var pb = global.productBuilder;
      if (pb && typeof pb.reconcileOfferStepAfterMarketFilter === 'function') {
        pb.reconcileOfferStepAfterMarketFilter();
      }
      var filter = global.PBOfferBundleMarketFilter;
      if (filter && typeof filter.applyToBuilderDataSync === 'function') {
        filter.applyToBuilderDataSync(true);
      }
      if (pb && typeof pb.renderOffers === 'function') pb.renderOffers({ intro: 'skip' });
      return true;
    });
  }

  function start() {
    var doc = global.document;
    if (!doc) return;
    // The button first: one request, and the thing a shopper can press right now.
    refreshTriggerButton();
    // The rest on the way in, not at page load — most visitors never open the builder, and the
    // click fires long before the offers step can paint, so the answers land in time. A late one
    // still lands: the pass re-renders the step itself.
    doc.addEventListener(
      'click',
      function (e) {
        var t = e && e.target;
        if (!t || !t.closest) return;
        if (t.closest('#openBuilderBtn') || t.closest('[data-pb-open-builder]')) refreshBundleStock();
      },
      true
    );
  }

  var api = {
    productJsonUrl: productJsonUrl,
    readAvailability: readAvailability,
    collectBundleHandles: collectBundleHandles,
    fetchAvailability: fetchAvailability,
    applyToBuilderData: applyToBuilderData,
    applyToTriggerButton: applyToTriggerButton,
    refreshTriggerButton: refreshTriggerButton,
    refreshBundleStock: refreshBundleStock,
    SOLD_OUT_LABEL: SOLD_OUT_LABEL,
    RELOAD_FLAG_PREFIX: RELOAD_FLAG_PREFIX
  };

  global.PBLiveStock = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (global.document) {
    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
